import { createContext, useContext } from 'react';

/**
 * What each lesson of a program built in the Capability build flow is for: the
 * one capability it builds, or the learning outcomes it serves (never both),
 * and whether it ends on a graded Roleplay. The backend tags the structure it
 * generates and keeps the tags through edits (a lesson sent without them keeps
 * the ones stored under its title); it refuses a structure that leaves one of
 * the program's capabilities with no lesson (409).
 *
 * The build flow gives the wizard the program's capabilities and outcomes, so
 * the Structure step can name them and ask what a lesson added by hand is for.
 * The standalone wizard has none of this: there the context is null.
 */
export interface LessonTags {
  /** The capabilities the program builds, by id, with their names. */
  capabilities: Array<{ id: string; name: string }>;
  /** Its other learning outcomes. */
  outcomes: string[];
}

/** A lesson as the structure tags it. */
export interface TaggedLesson {
  title: string;
  capabilityId?: string | null;
  outcomes?: string[] | null;
  roleplay?: boolean;
}

/** What a lesson added by hand is made to be for: a capability it builds, or an outcome it serves. */
export type LessonTagChoice = { capabilityId: string } | { outcome: string };

export const LessonTagsContext = createContext<LessonTags | null>(null);

/** The program's capabilities and outcomes, inside the build flow; null in the standalone wizard. */
export const useLessonTags = (): LessonTags | null => useContext(LessonTagsContext);

/** The capability a lesson builds, when it is one the program builds. */
export const lessonCapability = (lesson: TaggedLesson, tags: LessonTags): { id: string; name: string } | null =>
  tags.capabilities.find((c) => c.id === lesson.capabilityId) ?? null;

/**
 * Which lessons end on a graded Roleplay, module by module: for each of the
 * program's capabilities its last lesson, and the one halfway through its
 * lessons too when it has four or more. Lessons for learning outcomes never
 * do. The backend's own rule, applied to the structure as it stands, so an
 * edit shows where the Roleplays will be before it is saved.
 */
export const roleplayFlags = (modules: Array<{ lessons: TaggedLesson[] }>, tags: LessonTags): boolean[][] => {
  const known = new Set(tags.capabilities.map((c) => c.id));
  const byCapability = new Map<string, number[]>();
  const lessons = modules.flatMap((m) => m.lessons);
  lessons.forEach((l, i) => {
    if (!l.capabilityId || !known.has(l.capabilityId)) return;
    byCapability.set(l.capabilityId, [...(byCapability.get(l.capabilityId) ?? []), i]);
  });
  const flags = lessons.map(() => false);
  byCapability.forEach((at) => {
    flags[at[at.length - 1]] = true;
    if (at.length >= 4) flags[at[Math.floor((at.length - 1) / 2)]] = true;
  });
  let i = 0;
  return modules.map((m) => m.lessons.map(() => flags[i++]));
};

/**
 * Which lessons carry a Scored Roleplay as saved. Once a course's lessons are
 * written the flags are the admin's: the backend keeps each lesson's in step
 * with its Roleplay toggle and an edit never moves one, so the editors show
 * these instead of the plan (docs/PRD-capability-lesson-roleplays.md D3).
 */
export const savedRoleplayFlags = (modules: Array<{ lessons: TaggedLesson[] }>): boolean[][] =>
  modules.map((m) => m.lessons.map((l) => Boolean(l.capabilityId) && l.roleplay === true));

/** The program's capabilities that no lesson builds: the backend refuses the structure until each has one. */
export const uncoveredCapabilities = (
  modules: Array<{ lessons: TaggedLesson[] }>,
  tags: LessonTags
): Array<{ id: string; name: string }> =>
  tags.capabilities.filter((c) => !modules.some((m) => m.lessons.some((l) => l.capabilityId === c.id)));

/** The structure with one lesson made to build a capability, or to serve one outcome. */
export const withLessonTag = <M extends { lessons: TaggedLesson[] }>(
  modules: M[],
  moduleIndex: number,
  lessonIndex: number,
  choice: LessonTagChoice
): M[] =>
  modules.map((m, mi) =>
    mi !== moduleIndex
      ? m
      : {
          ...m,
          lessons: m.lessons.map((l, li) =>
            li !== lessonIndex
              ? l
              : 'capabilityId' in choice
              ? { ...l, capabilityId: choice.capabilityId, outcomes: [] }
              : { ...l, capabilityId: null, outcomes: [choice.outcome] }
          )
        }
  );

/**
 * A lesson's tags as the generation endpoints take them. Nothing for a lesson
 * that has none: the backend then keeps the tags it stored under its title.
 */
export const lessonTagFields = (lesson: TaggedLesson): { capabilityId?: string; outcomes?: string[] } => {
  if (lesson.capabilityId) return { capabilityId: lesson.capabilityId };
  return lesson.outcomes && lesson.outcomes.length > 0 ? { outcomes: lesson.outcomes } : {};
};
